import { useMemo, useRef } from "react";

import styles from "./index.module.scss";

function digitsOnly(value: string) {
  return (value ?? "").replace(/\D/g, "");
}

function normalizePhoneDigits(input: string) {
  let digits = digitsOnly(input ?? "");
  if (!digits) return "";

  // 1st digit: must be 7 (if not 7 -> replace to 7)
  if (digits[0] !== "7") digits = `7${digits.slice(1)}`;

  // 2nd digit: cannot be 7 or 8 (keep existing behavior)
  if (digits.length > 1 && (digits[1] === "7" || digits[1] === "8")) {
    digits = `7${digits.slice(2)}`;
  }

  return digits.slice(0, 11);
}

function formatPartialPhone(digits: string) {
  const d = normalizePhoneDigits(digits) || "7";
  const rest = d.slice(1); // up to 10 digits

  const a = rest.slice(0, 3);
  const b = rest.slice(3, 6);
  const c = rest.slice(6, 8);
  const e = rest.slice(8, 10);

  let out = "+7";
  if (rest.length > 0) out += " (";
  if (a) out += a;
  if (rest.length >= 3) out += ") ";
  if (b) out += b;
  if (rest.length >= 6) out += " ";
  if (c) out += c;
  if (rest.length >= 8) out += "-";
  if (e) out += e;

  // If we ended with punctuation (space, "-", etc) — trim it off
  while (out.length > 0 && !/\d$/.test(out)) {
    out = out.slice(0, -1);
  }

  return out;
}

type PhoneInputCompactProps = {
  id?: string;
  dataTest?: string;
  placeholder?: string;
  value?: string;
  disabled?: boolean;
  onChange: (next: string) => void;
  onBlur: (next: string) => void;
};

export function PhoneInputCompact({
  id,
  dataTest,
  placeholder,
  value,
  disabled,
  onChange,
  onBlur,
}: PhoneInputCompactProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const normalizedDigits = useMemo(
    () => normalizePhoneDigits(value ?? ""),
    [value],
  );
  const displayValue = useMemo(() => {
    // Always keep +7 visible, even when empty/invalid
    return formatPartialPhone(normalizedDigits || "7");
  }, [normalizedDigits]);

  const showPlaceholder = normalizePhoneDigits(value ?? "").length <= 1;

  const commit = (raw: string) => {
    const nextDigits = normalizePhoneDigits(raw);
    onChange(formatPartialPhone(nextDigits || "7"));
  };

  return (
    <>
      <input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        data-test={dataTest}
        placeholder={placeholder}
        ref={inputRef}
        disabled={disabled}
        value={displayValue}
        onChange={(event) => commit(event.target.value)}
        onBlur={(event) => {
          commit(event.target.value);
          onBlur(event.target.value);
        }}
      />

      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <span
        className={styles.PhonePlaceholder}
        style={{ display: !disabled && showPlaceholder ? undefined : "none" }}
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        onMouseDown={(e) => {
          e.preventDefault();
          if (disabled) return;
          requestAnimationFrame(() => inputRef.current?.focus());
        }}
      >
        (___) ___ __-__
      </span>
    </>
  );
}
